import { create } from 'zustand';

export const useAuthStore = create((set, get) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  currentOrganization: null,
  isInitialized: false,
  pendingMFA: false,
  mfaTempToken: null,
  pendingTokens: null,

  initialize: () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('mmpr_tokens') || 'null');
      const user = JSON.parse(localStorage.getItem('mmpr_user') || 'null');
      const orgId = localStorage.getItem('mmpr_current_org') || null;

      if (tokens?.access && user) {
        set({
          tokens,
          user,
          isAuthenticated: true,
          currentOrganization: orgId,
          isInitialized: true,
        });
        return;
      }

      // Restore pending MFA state across page reloads
      const pendingMFAData = JSON.parse(sessionStorage.getItem('mmpr_pending_mfa') || 'null');
      if (pendingMFAData?.user) {
        set({
          user: pendingMFAData.user,
          pendingMFA: true,
          mfaTempToken: pendingMFAData.mfaTempToken || null,
          pendingTokens: pendingMFAData.pendingTokens || null,
          isAuthenticated: false,
          isInitialized: true,
        });
        return;
      }

      set({ isInitialized: true });
    } catch {
      localStorage.removeItem('mmpr_tokens');
      localStorage.removeItem('mmpr_user');
      localStorage.removeItem('mmpr_current_org');
      sessionStorage.removeItem('mmpr_pending_mfa');
      set({ isInitialized: true });
    }
  },

  setMFAPending: (user, tempToken) => {
    sessionStorage.setItem('mmpr_pending_mfa', JSON.stringify({ user, mfaTempToken: tempToken }));
    set({
      user,
      pendingMFA: true,
      mfaTempToken: tempToken,
      pendingTokens: null,
      isAuthenticated: false,
    });
  },

  setPendingMFAWithTokens: (user, tokens) => {
    sessionStorage.setItem('mmpr_pending_mfa', JSON.stringify({ user, pendingTokens: tokens }));
    set({
      user,
      pendingMFA: true,
      mfaTempToken: null,
      pendingTokens: tokens,
      isAuthenticated: false,
    });
  },

  clearMFA: () => {
    sessionStorage.removeItem('mmpr_pending_mfa');
    set({ pendingMFA: false, mfaTempToken: null, pendingTokens: null });
  },

  completeMFALogin: (user, tokens) => {
    sessionStorage.removeItem('mmpr_pending_mfa');
    localStorage.setItem('mmpr_tokens', JSON.stringify(tokens));
    localStorage.setItem('mmpr_user', JSON.stringify(user));

    const orgList = user.organizations || [];
    const savedOrgId = localStorage.getItem('mmpr_current_org');
    const isStillValid = orgList.some((o) => String(o.id) === String(savedOrgId));
    const orgId = savedOrgId && isStillValid ? savedOrgId : (orgList[0]?.id ? String(orgList[0].id) : null);

    if (orgId) {
      localStorage.setItem('mmpr_current_org', orgId);
    } else {
      localStorage.removeItem('mmpr_current_org');
    }

    set({
      user,
      tokens,
      isAuthenticated: true,
      currentOrganization: orgId,
      pendingMFA: false,
      mfaTempToken: null,
      pendingTokens: null,
    });
  },

  login: (user, tokens) => {
    localStorage.setItem('mmpr_tokens', JSON.stringify(tokens));
    localStorage.setItem('mmpr_user', JSON.stringify(user));

    const orgList = user.organizations || [];
    const savedOrgId = localStorage.getItem('mmpr_current_org');
    // Reuse the last active org if it's still in this user's org list; otherwise fall back to first.
    const isStillValid = orgList.some((o) => String(o.id) === String(savedOrgId));
    const orgId = savedOrgId && isStillValid ? savedOrgId : (orgList[0]?.id ? String(orgList[0].id) : null);

    if (orgId) {
      localStorage.setItem('mmpr_current_org', orgId);
    } else {
      localStorage.removeItem('mmpr_current_org');
    }

    set({
      user,
      tokens,
      isAuthenticated: true,
      currentOrganization: orgId,
    });
  },

  logout: () => {
    localStorage.removeItem('mmpr_tokens');
    localStorage.removeItem('mmpr_user');
    localStorage.removeItem('mmpr_current_org');

    sessionStorage.removeItem('mmpr_pending_mfa');
    set({
      user: null,
      tokens: null,
      isAuthenticated: false,
      currentOrganization: null,
      pendingMFA: false,
      mfaTempToken: null,
      pendingTokens: null,
    });
  },

  setUser: (user) => {
    localStorage.setItem('mmpr_user', JSON.stringify(user));
    set({ user });
  },

  setOrganization: (orgId) => {
    if (orgId) {
      localStorage.setItem('mmpr_current_org', orgId);
    } else {
      localStorage.removeItem('mmpr_current_org');
    }
    set({ currentOrganization: orgId });
  },

  setTokens: (tokens) => {
    localStorage.setItem('mmpr_tokens', JSON.stringify(tokens));
    set({ tokens });
  },

  getAccessToken: () => get().tokens?.access || null,

  /**
   * Check a single permission key against the user's effective permissions
   * (embedded in the login token by the backend).
   * Superusers bypass all checks; all other roles (including HR_ADMIN) use
   * the permissions object returned by the backend.
   */
  hasPermission: (key) => {
    const { user } = get();
    if (!user) return false;
    if (user.is_superuser) return true;
    return user.permissions?.[key] === true;
  },

  hasRole: (role) => {
    const { user } = get();
    return user?.role === role;
  },

  hasAnyRole: (...roles) => {
    const { user } = get();
    return roles.includes(user?.role);
  },

  /**
   * Returns the current user's scope_type string (e.g. 'GLOBAL', 'AREA',
   * 'ORGANIZATION', 'SELF') as supplied by the login token response.
   * Falls back to 'SELF' for unauthenticated or legacy users.
   */
  getScopeType: () => {
    const { user } = get();
    return user?.scope_type ?? 'SELF';
  },

  /**
   * Returns true if the current user has multi-org switching enabled.
   * Driven by scope_type from backend — no hardcoded role names here.
   */
  isMultiOrgUser: () => {
    const { user } = get();
    return (user?.scope_type === 'AREA') && (user?.organizations?.length > 1);
  },
}));

export default useAuthStore;
